import { Link, useLocation, useNavigate } from 'react-router-dom';
import { serverEndpoints } from '@/config/serverEndpoints';
import CopyableIp from '@/components/base/CopyableIp';
import { useCommunity } from '@/context/CommunityContext';
import { useLanguage } from '@/localization';

/**
 * Footer component displaying site footer with links and server information.
 *
 * @description Provides footer content including quick navigation links, server
 * information, social media links, and copyright information. Includes smooth
 * scrolling navigation to different sections of the page.
 *
 * @returns {JSX.Element} A footer component
 */
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();
  const { discordInvite, social } = useCommunity();
  const { t } = useLanguage();
  const steamHref = social.find((s) => s.icon === 'steam')?.href ?? 'https://steamcommunity.com/groups/uliunailt';

  /**
   * Navigates to the home page and scrolls to a specific section.
   *
   * @param {string} sectionId - The ID of the section element to scroll to
   */
  const goToSection = (sectionId: string) => {
    const scroll = () => {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    };

    if (location.pathname === '/') {
      scroll();
    } else {
      navigate('/');
      setTimeout(scroll, 100);
    }
  };

  return (
    <footer className="spark-wire relative bg-black/80 border-t border-red-900/30">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <img src="/brand/ul-logo.png" alt="Uliunai" className="w-10 h-10 object-contain" />
              <h3 className="text-2xl font-bold text-white font-orbitron">
                <span className="text-red-500">Uliunai</span>.com
              </h3>
            </div>
            <p className="text-gray-400 mb-6 max-w-md">
              {t(
                'The longest-running Killing Floor 1 server with custom content, active community, and years of reliable service. Join thousands of players in the ultimate zombie survival experience.',
              )}
            </p>
            <div className="flex gap-4">
              <a href={discordInvite} target="_blank" rel="noopener noreferrer" aria-label="Discord" className="w-10 h-10 bg-gray-800 hover:bg-red-600 hover:shadow-red-glow rounded-lg flex items-center justify-center transition-colors cursor-pointer">
                <i className="ri-discord-fill text-white text-lg" aria-hidden="true"></i>
              </a>
              <a href={steamHref} target="_blank" rel="noopener noreferrer" aria-label="Steam" className="w-10 h-10 bg-gray-800 hover:bg-red-600 hover:shadow-red-glow rounded-lg flex items-center justify-center transition-colors cursor-pointer">
                <i className="ri-steam-fill text-white text-lg" aria-hidden="true"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-bold text-white mb-4 font-orbitron">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => goToSection('home')}
                  className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => goToSection('about')}
                  className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  About
                </button>
              </li>
              <li>
                <button
                  onClick={() => goToSection('server-info')}
                  className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Server Info
                </button>
              </li>
              <li>
                <Link
                  to="/download"
                  className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Downloads
                </Link>
              </li>
              <li>
                <button
                  onClick={() => goToSection('vip')}
                  className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  VIP Membership
                </button>
              </li>
              <li>
                <Link
                  to="/rules"
                  className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Community Rules
                </Link>
              </li>
              <li>
                <button
                  onClick={() => goToSection('contact')}
                  className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold text-white mb-4 font-orbitron">Server Info</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <strong className="text-red-400">{t('IP:')}</strong> <CopyableIp className="align-middle text-gray-400 hover:text-red-300" />
              </li>
              <li>
                <strong className="text-red-400">{t('Game:')}</strong> Killing Floor 1
              </li>
              <li>
                <strong className="text-red-400">{t('Max Players:')}</strong> 16
              </li>
              <li>
                <strong className="text-red-400">{t('Location:')}</strong> {serverEndpoints.direct.location}
              </li>
              <li>
                <strong className="text-red-400">{t('Uptime:')}</strong> 99.9%
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              {t('Copyright')} {currentYear} Uliunai.com. {t('All rights reserved.')}{' '}
              {t('Not affiliated with Tripwire Interactive.')} {t('Designed by')} Litenweb.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-4 md:mt-0">
              <a href="https://uliunai.com/privacy" className="text-gray-400 hover:text-red-400 text-sm transition-colors whitespace-nowrap">
                Privacy Policy
              </a>
              <a href="https://uliunai.com/terms" className="text-gray-400 hover:text-red-400 text-sm transition-colors whitespace-nowrap">
                Terms of Service
              </a>
              <Link to="/rules" className="text-gray-400 hover:text-red-400 text-sm transition-colors whitespace-nowrap">
                Rules
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
